import { useEffect, useRef, useState, type FormEvent } from 'react';
import { koreaDate } from '@chagok/domain';
import { api, ApiError, newId } from '../api/client';
import type { AiConfirmField, AiParse, AiStatus, Transaction } from '../api/types';
import { childCategories, paymentMethods, topCategories, withCurrent } from '../lib/catalog';
import { krw, parseAmountInput } from '../lib/format';
import { periodFrom } from '../lib/period';
import { clearDraft, loadDraft, saveDraft } from '../lib/storage';
import { useApp, type EntryRequest } from '../state';

type FormKind = 'income' | 'expense' | 'transfer' | 'refund';

interface Form {
  kind: FormKind;
  amount: string;
  effectiveDate: string;
  merchant: string;
  memo: string;
  categoryId: string;
  subcategoryId: string;
  paymentMethodId: string;
  fromAccountId: string;
  toAccountId: string;
}

interface Draft {
  form: Form;
  /** 재전송해도 한 번만 저장되도록 초안과 함께 보관한다. */
  transactionId: string;
  idempotencyKey: string;
  /** AI 한 줄 입력 원문. AI 요청이 실패해도 초안에 남는다(설계서 7장). */
  aiText?: string;
  aiNote?: { explanation: string; needsConfirmation: AiConfirmField[] } | null;
  /** AI가 채운 초안을 확인해 저장하면 source=ai */
  source?: 'manual' | 'ai';
  editRevision?: number;
  pendingWrite?: { payload: Record<string, unknown>; ifMatch?: number };
}

const DRAFT_KEY = 'entry';
const CONFIRM_LABELS: Record<AiConfirmField, string> = { kind: '종류', amountKrw: '금액', effectiveDate: '날짜', merchant: '사용처', category: '분류', paymentMethod: '수단' };

/** AI 사용 여부는 대화상자를 열 때 한 번만 묻는다. 실패하면 꺼진 것으로 본다(수동 입력은 그대로). */
let aiStatus: Promise<boolean> | null = null;
const aiEnabled = () => (aiStatus ??= api.get<{ data: AiStatus }>('/ai/status').then((r) => r.data.enabled, () => ((aiStatus = null), false)));

function emptyForm(kind: FormKind): Form {
  return { kind, amount: '', effectiveDate: koreaDate(), merchant: '', memo: '', categoryId: '', subcategoryId: '', paymentMethodId: '', fromAccountId: '', toAccountId: '' };
}

function formFrom(tx: Transaction): Form {
  return {
    kind: tx.kind,
    amount: tx.amountKrw,
    effectiveDate: tx.effectiveDate,
    merchant: tx.merchant ?? '',
    memo: tx.memo,
    categoryId: tx.categoryId ?? '',
    subcategoryId: tx.subcategoryId ?? '',
    paymentMethodId: tx.paymentMethodId ?? '',
    fromAccountId: tx.fromAccountId ?? '',
    toAccountId: tx.toAccountId ?? ''
  };
}

export function EntryDialog() {
  const { entryRequest } = useApp();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (entryRequest && !dialog.open) dialog.showModal();
    if (!entryRequest && dialog.open) dialog.close();
  }, [entryRequest]);
  return <dialog ref={ref}>{entryRequest && <EntryForm request={entryRequest} dialogRef={ref} />}</dialog>;
}

function EntryForm({ request, dialogRef }: { request: EntryRequest; dialogRef: React.RefObject<HTMLDialogElement | null> }) {
  const { catalog, closeEntry, changed, toast, reloadCatalog, period, setPeriod } = useApp();
  const mode = request.edit ? 'edit' : request.refundOf ? 'refund' : 'create';
  const draftKey = request.edit ? `edit:${request.edit.id}` : request.refundOf ? `refund:${request.refundOf.id}` : DRAFT_KEY;

  const [draft, setDraft] = useState<Draft>(() => {
    const saved = loadDraft<Draft>(draftKey);
    if (saved && (mode !== 'create' || saved.pendingWrite || !request.kind || saved.form.kind === request.kind)) return saved;
    if (request.edit) return { form: formFrom(request.edit), transactionId: request.edit.id, idempotencyKey: newId(), editRevision: request.edit.revision };
    if (request.refundOf) {
      const o = request.refundOf;
      return {
        form: { ...emptyForm('refund'), merchant: o.merchant ?? '', categoryId: o.categoryId ?? '', subcategoryId: o.subcategoryId ?? '', paymentMethodId: o.paymentMethodId ?? '' },
        transactionId: newId(),
        idempotencyKey: newId()
      };
    }
    return { form: emptyForm(request.kind ?? 'expense'), transactionId: newId(), idempotencyKey: newId() };
  });
  const restored = useRef(loadDraft<Draft>(draftKey)?.transactionId === draft.transactionId);
  const [baseRevision, setBaseRevision] = useState(draft.editRevision ?? request.edit?.revision ?? 0);
  const [conflict, setConflict] = useState<Transaction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState<boolean | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const version = useRef(0);
  useEffect(() => () => { version.current += 1; }, []);
  const form = draft.form;
  const update = (fn: (d: Draft) => Draft) => {
    version.current += 1;
    setDraft((d) => {
      const next = fn(d);
      saveDraft(draftKey, next);
      return next;
    });
  };
  const set = (patch: Partial<Form>) => update((d) => ({ ...d, form: { ...d.form, ...patch } }));
  const confirm = new Set(draft.aiNote?.needsConfirmation ?? []);
  const needsCheck = (field: AiConfirmField) => confirm.has(field) && <small className="confirm-needed">확인 필요</small>;

  useEffect(() => {
    if (mode === 'create') void aiEnabled().then(setAi);
  }, [mode]);

  /** 문장 → 초안. 저장하지 않는다. AI가 비워 둔 항목은 이미 입력한 값을 지우지 않는다. */
  async function fillWithAi() {
    const text = (draft.aiText ?? '').trim();
    if (!text || aiBusy || busy || draft.pendingWrite) return;
    const current = version.current;
    setAiBusy(true);
    setAiError(null);
    try {
      const { data } = await api.post<{ data: AiParse }>('/ai/parse', { text });
      if (current !== version.current) {
        setAiError('입력 내용이 바뀌어 이전 AI 응답을 적용하지 않았어요. 다시 요청해주세요.');
        return;
      }
      const d = data.draft;
      if ([d.categoryId, d.paymentMethodId].some((id) => id && !catalog.categories.some((c) => c.id === id) && !catalog.paymentMethods.some((p) => p.id === id))) void reloadCatalog();
      update((prev) => {
        const f = prev.form;
        const kind = d.kind ?? (f.kind === 'income' ? 'income' : 'expense');
        const kindChanged = kind !== f.kind;
        return {
          ...prev,
          source: 'ai',
          aiNote: { explanation: data.explanation, needsConfirmation: data.needsConfirmation },
          form: {
            ...f,
            kind,
            amount: d.amountKrw ?? f.amount,
            effectiveDate: d.effectiveDate ?? f.effectiveDate,
            merchant: d.merchant ?? f.merchant,
            categoryId: d.categoryId ?? (kindChanged ? '' : f.categoryId),
            subcategoryId: d.subcategoryId ?? (kindChanged ? '' : f.subcategoryId),
            paymentMethodId: d.paymentMethodId ?? (kindChanged ? '' : f.paymentMethodId),
            fromAccountId: '',
            toAccountId: ''
          }
        };
      });
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      setAiError(e.fields.text ?? e.message);
    } finally {
      setAiBusy(false);
    }
  }

  useEffect(() => {
    const dialog = dialogRef.current;
    // 이전에 닫힌 대화상자의 close 이벤트가 다시 연 뒤에 도착할 수 있다. 실제로 닫혀 있을 때만 처리한다.
    const onCancel = () => {
      if (!dialog?.open) closeEntry();
    };
    dialog?.addEventListener('close', onCancel);
    return () => dialog?.removeEventListener('close', onCancel);
  }, [dialogRef, closeEntry]);

  const catalogKind = form.kind === 'income' ? 'income' : 'expense';
  const allTops = topCategories(catalog, catalogKind, true);
  const tops = withCurrent(topCategories(catalog, catalogKind), allTops, mode === 'edit' ? request.edit?.categoryId : null);
  const allSubs = childCategories(catalog, form.categoryId, true);
  const subs = withCurrent(childCategories(catalog, form.categoryId), allSubs, mode === 'edit' ? request.edit?.subcategoryId : null);
  const allPayments = paymentMethods(catalog, catalogKind, true);
  const payments = withCurrent(paymentMethods(catalog, catalogKind), allPayments, mode === 'edit' ? request.edit?.paymentMethodId : null);
  const accounts = catalog.accounts.filter((a) => a.active || [request.edit?.fromAccountId, request.edit?.toAccountId].includes(a.id));
  const income = form.kind === 'income';

  function body() {
    const amountKrw = parseAmountInput(form.amount);
    if (!amountKrw) {
      setFields({ amountKrw: '1원 이상의 정수로 입력해주세요.' });
      return null;
    }
    if (form.kind === 'transfer') {
      return { amountKrw, effectiveDate: form.effectiveDate, merchant: form.merchant || null, memo: form.memo, fromAccountId: form.fromAccountId || null, toAccountId: form.toAccountId || null };
    }
    return {
      amountKrw,
      effectiveDate: form.effectiveDate,
      merchant: form.merchant || null,
      memo: form.memo,
      ...(form.kind !== 'refund' && { categoryId: form.categoryId || null, subcategoryId: form.subcategoryId || null }),
      paymentMethodId: form.paymentMethodId || null
    };
  }

  async function submit(event: FormEvent, overrideRevision?: number) {
    event.preventDefault();
    if (busy || aiBusy) return;
    const payload = draft.pendingWrite?.payload ?? body();
    if (!payload) return;
    const write = draft.pendingWrite ?? { payload, ifMatch: overrideRevision ?? baseRevision };
    update((d) => ({ ...d, pendingWrite: write, ...(mode === 'edit' && { editRevision: write.ifMatch }) }));
    setBusy(true);
    setError(null);
    setFields({});
    try {
      let saved: Transaction;
      if (mode === 'edit') {
        const res = await api.patch<{ data: Transaction }>(`/transactions/${draft.transactionId}`, payload, {
          ifMatch: write.ifMatch,
          idempotencyKey: draft.idempotencyKey
        });
        saved = res.data;
      } else {
        const res = await api.post<{ data: Transaction }>(
          '/transactions',
          { id: draft.transactionId, kind: form.kind, source: mode === 'create' ? (draft.source ?? 'manual') : 'manual', ...payload, ...(mode === 'refund' && { refundOfId: request.refundOf!.id }) },
          { idempotencyKey: draft.idempotencyKey }
        );
        saved = res.data;

      }
      clearDraft(draftKey);
      if (saved.effectiveDate < period.start || saved.effectiveDate > period.end) setPeriod(periodFrom('months', saved.effectiveDate.slice(0, 7), saved.effectiveDate.slice(0, 7)));
      changed();
      toast(mode === 'edit' ? '수정했습니다.' : '기록을 저장했어요. 대시보드에 반영했습니다.');
      closeEntry();
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      if (e.status > 0 && e.status < 500) update((d) => ({ ...d, pendingWrite: undefined }));
      if (e.code === 'REVISION_CONFLICT') {
        setConflict((e.details as { current: Transaction }).current);
      } else {
        // 네트워크 오류 등: 초안과 ID를 유지한다. 다시 저장하면 같은 ID로 보내 한 번만 저장된다.
        if (e.status > 0 && e.status < 500) update((d) => ({ ...d, idempotencyKey: newId() }));
        setError(e.message);
        setFields(e.fields);
        if (e.code === 'CATEGORY_ARCHIVED' || e.code === 'PAYMENT_METHOD_ARCHIVED') void reloadCatalog();
      }
    } finally {
      setBusy(false);
    }
  }

  const title = mode === 'edit' ? '기록 수정' : mode === 'refund' ? '환불 기록' : '수입·지출 기록';
  return (
    <form onSubmit={(e) => void submit(e)}>
      <fieldset className="entry-fields" disabled={busy || !!draft.pendingWrite}>
      <div className="card-head">
        <h2>{title}</h2>
        <button type="button" className="close" aria-label="닫기" onClick={closeEntry}>
          ×
        </button>
      </div>
      {mode === 'create' && (
        <div className="segmented" role="radiogroup" aria-label="기록 종류">
          {(['expense', 'income', 'transfer'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={form.kind === k} className={form.kind === k ? 'on' : ''} onClick={() => update((d) => ({ ...d, aiNote: null, form: { ...emptyForm(k), amount: form.amount, effectiveDate: form.effectiveDate, merchant: form.merchant, memo: form.memo } }))}>
              {k === 'expense' ? '지출' : k === 'income' ? '수입' : '이체'}
            </button>
          ))}
        </div>
      )}
      {restored.current && <p className="hint">작성 중이던 초안을 불러왔어요.</p>}
      {mode === 'refund' && request.refundOf && (
        <p className="muted">
          ‘{request.refundOf.merchant ?? '지출'}’ {krw(request.refundOf.amountKrw)}의 환불입니다. 환불은 원래 지출의 분류({request.refundOf.categoryName} › {request.refundOf.subcategoryName})로 집계됩니다.
        </p>
      )}
      {mode === 'create' && form.kind !== 'transfer' && ai && (
        <div className="ai-entry">
          <label htmlFor="ai-text">✧ 한 줄로 입력하기</label>
          <div className="ai-row">
            <input
              id="ai-text"
              value={draft.aiText ?? ''}
              maxLength={1000}
              placeholder="예: 메가커피에서 3000원 카드 결제함"
              onChange={(e) => update((d) => ({ ...d, aiText: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void fillWithAi();
                }
              }}
            />
            <button type="button" onClick={() => void fillWithAi()} disabled={aiBusy || !(draft.aiText ?? '').trim()}>
              {aiBusy ? '읽는 중…' : 'AI로 채우기'}
            </button>
          </div>
          {aiError ? (
            <small className="error" role="alert">
              {aiError} 아래에서 직접 입력할 수 있어요.
            </small>
          ) : draft.aiNote ? (
            <small className="ai-note">
              {draft.aiNote.explanation}
              {draft.aiNote.needsConfirmation.length > 0 && ` · 확인 필요: ${draft.aiNote.needsConfirmation.map((f) => CONFIRM_LABELS[f]).join(', ')}`}
            </small>
          ) : (
            <small className="hint">AI가 아래 칸을 채워요. 확인하고 저장해야 기록됩니다.</small>
          )}
        </div>
      )}
      {mode === 'create' && ai === false && <p className="muted">AI 한 줄 입력이 꺼져 있어요. 직접 입력해주세요.</p>}

      {conflict && (
        <div className="conflict" role="alert">
          <b>다른 기기에서 먼저 수정했습니다.</b>
          <p className="hint">자동으로 덮어쓰지 않았습니다. 두 값을 비교하고 선택해주세요.</p>
          <table>
            <tbody>
              <tr>
                <th />
                <th>서버의 최신 값</th>
                <th>내가 입력한 값</th>
              </tr>
              <tr>
                <td>금액</td>
                <td>{krw(conflict.amountKrw)}</td>
                <td>{parseAmountInput(form.amount) ? krw(parseAmountInput(form.amount)!) : form.amount}</td>
              </tr>
              <tr>
                <td>날짜</td>
                <td>{conflict.effectiveDate}</td>
                <td>{form.effectiveDate}</td>
              </tr>
              <tr>
                <td>내용</td>
                <td>{conflict.merchant ?? '-'}</td>
                <td>{form.merchant || '-'}</td>
              </tr>
              <tr>
                <td>분류</td>
                <td>
                  {conflict.categoryName} › {conflict.subcategoryName}
                </td>
                <td>
                  {catalog.categories.find((c) => c.id === form.categoryId)?.name} › {catalog.categories.find((c) => c.id === form.subcategoryId)?.name}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="row-actions">
            <button
              type="button"
              onClick={() => {
                update((d) => ({ ...d, form: formFrom(conflict), editRevision: conflict.revision, idempotencyKey: newId() }));
                setBaseRevision(conflict.revision);
                setConflict(null);
              }}
            >
              최신 값으로 다시 편집
            </button>
            <button
              type="button"
              className="primary"
              onClick={(e) => {
                const revision = conflict.revision;
                setBaseRevision(revision);
                setDraft((d) => ({ ...d, idempotencyKey: newId() }));
                setConflict(null);
                void submit(e as unknown as FormEvent, revision);
              }}
            >
              내 값으로 저장
            </button>
          </div>
        </div>
      )}

      <div className="form-grid">
        <label>
          금액 (원) {needsCheck('amountKrw')}
          <input inputMode="numeric" value={form.amount} onChange={(e) => set({ amount: e.target.value })} required aria-invalid={!!fields.amountKrw} placeholder="예: 3000" />
          {fields.amountKrw && <small className="error">{fields.amountKrw}</small>}
        </label>
        <label>
          {income ? '입금일' : form.kind === 'transfer' ? '이체일' : form.kind === 'refund' ? '환불일' : '사용일'} {needsCheck('effectiveDate')}
          <input type="date" value={form.effectiveDate} onChange={(e) => set({ effectiveDate: e.target.value })} required />
        </label>
        <label className="full">
          {income ? '수입원 / 내용' : '사용처 / 내용'} {needsCheck('merchant')}
          <input value={form.merchant} maxLength={100} onChange={(e) => set({ merchant: e.target.value })} />
        </label>

        {form.kind === 'transfer' ? (
          accounts.length < 2 ? (
            <p className="hint full">이체는 계좌가 두 개 이상 필요합니다. 항목 설정에서 계좌를 추가해주세요.</p>
          ) : (
            <>
              <label>
                보내는 계좌
                <select value={form.fromAccountId} onChange={(e) => set({ fromAccountId: e.target.value })} required>
                  <option value="">선택 필요</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.active ? '' : ' (삭제됨)'}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                받는 계좌
                <select value={form.toAccountId} onChange={(e) => set({ toAccountId: e.target.value })} required>
                  <option value="">선택 필요</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.active ? '' : ' (삭제됨)'}
                    </option>
                  ))}
                </select>
              </label>
              <p className="hint full">이체(계좌 이동·적금 납입·카드 대금)는 수입/지출 합계에 넣지 않습니다.</p>
            </>
          )
        ) : form.kind === 'refund' ? null : tops.length === 0 ? (
          <p className="hint full error">선택할 분류가 없습니다. 항목 설정에서 분류를 추가하거나 복원해주세요.</p>
        ) : (
          <>
            <label>
              {income ? '수입 구분' : '대분류'} {needsCheck('category')}
              <select value={form.categoryId} onChange={(e) => set({ categoryId: e.target.value, subcategoryId: '' })} required aria-invalid={!!fields.categoryId}>
                <option value="">선택 필요</option>
                {tops.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.active ? '' : ' (삭제됨)'}
                  </option>
                ))}
              </select>
              {fields.categoryId && <small className="error">{fields.categoryId}</small>}
            </label>
            <label>
              소분류
              <select value={form.subcategoryId} onChange={(e) => set({ subcategoryId: e.target.value })} required disabled={!form.categoryId} aria-invalid={!!fields.subcategoryId}>
                <option value="">{form.categoryId && subs.length === 0 ? '소분류 없음 · 설정에서 추가' : '선택 필요'}</option>
                {subs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.active ? '' : ' (삭제됨)'}
                  </option>
                ))}
              </select>
              {fields.subcategoryId && <small className="error">{fields.subcategoryId}</small>}
            </label>
          </>
        )}

        {form.kind !== 'transfer' && (
          <label className="full">
            {income ? '입금수단' : '결제수단'} {needsCheck('paymentMethod')}
            <select value={form.paymentMethodId} onChange={(e) => set({ paymentMethodId: e.target.value })} aria-invalid={!!fields.paymentMethodId}>
              <option value="">선택 안 함</option>
              {payments.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.active ? '' : ' (삭제됨)'}
                </option>
              ))}
            </select>
            {fields.paymentMethodId && <small className="error">{fields.paymentMethodId}</small>}
          </label>
        )}
        <label className="full">
          메모 (선택)
          <input value={form.memo} maxLength={500} onChange={(e) => set({ memo: e.target.value })} />
        </label>
      </div>
      </fieldset>
      {draft.pendingWrite && !busy && <p className="notice warn">저장 결과를 아직 확인하지 못했어요. 중복 저장을 막기 위해 입력을 잠시 잠갔습니다. 아래 버튼으로 같은 요청을 다시 확인해주세요.</p>}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="primary full save" disabled={busy || aiBusy || !!conflict}>
        {busy ? '저장 중…' : draft.pendingWrite ? '저장 결과 다시 확인' : mode === 'edit' ? '수정 저장' : '확인하고 저장'}
      </button>
    </form>
  );
}
